import keys from '@debbie/common/dist/cache/keys.js';
import { useMutation } from '@debbie/common/dist/util/hooks.js';
import { TodoType } from '@debbie/common/dist/workflows/index.js';
import * as updatesLib from '@debbie/cortex/dist/api/updates.js';
import { c, h, notifications } from '@debbie/cortex/dist/index.js';
import q from '@debbie/cortex/dist/query.js';
import { updateTypesEntity, useUpdateTypes, useUpdateTypesMap, } from '@debbie/cortex/dist/update-types/index.js';
import { useWorkflows } from '@debbie/cortex/dist/workflows/index.js';
import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import THEME from '../theme.js';
import { errorNotification } from '../utils/errors.js';
import { ExternalLink, X, Zap } from './Icons/index.js';
import { Button, Checkbox, File, FileDropzone, Input, Modal, Panel, Select, UpdateTypeSelect, Well, } from './index.js';
import Loading from './Loading.js';
import { Section, SectionContentPanel } from './PanelSection.js';
import Procedure from './Workflows/Procedure.js';
import ProcedureCreator from './Workflows/ProcedureCreator.js';
import WYSIWYGEditor from './WYSIWYGEditor.js';
const UpdateTypeSettings = (props) => {
    const id = props.updateTypeId;
    const dropzoneRef = useRef(null);
    const lang = useSelector((s) => s.lang);
    const [label, setLabel] = useState(null);
    const [heroTextEditModalOpen, setHeroTextEditModalOpen] = useState(false);
    const updateTypesLoadable = useUpdateTypes();
    const updateTypes = h.hasData(updateTypesLoadable)
        ? updateTypesLoadable.data
        : null;
    const updateTypesMapLoadable = useUpdateTypesMap();
    const updateTypesMap = h.hasData(updateTypesMapLoadable)
        ? updateTypesMapLoadable.data
        : {};
    const workflowsLoadable = useWorkflows();
    const workflows = h.hasData(workflowsLoadable) ? workflowsLoadable.data : null;
    const updateType = updateTypes &&
        updateTypes.find((updateType) => updateType.updateTypeId === id);
    const editable = !updateType?.public;
    const saveMutation = useMutation(async (change) => {
        if (!updateType) {
            return;
        }
        await updatesLib.patchType(updateType.updateTypeId, change);
        q.invalidate(keys.write.modify(updateTypesEntity, updateType.updateTypeId));
    }, {
        onSuccess: () => {
            notifications.notification({
                title: `${lang.inputs.saved}!`,
            });
        },
        onError: (err) => {
            errorNotification(lang, err, 'update', lang.errorEntities.update);
        },
    });
    const deleteMutation = useMutation(async () => {
        if (!updateType) {
            return;
        }
        await updatesLib.delType(updateType.updateTypeId);
        q.invalidate(keys.write.modify(updateTypesEntity, updateType.updateTypeId));
    }, {
        onSuccess: () => {
            c.redirect.redirect('/settings/workflows/tasks');
        },
        onError: (err) => {
            errorNotification(lang, err, 'delete', lang.errorEntities.update);
        },
    });
    const save = (change) => {
        if (updateType) {
            saveMutation.mutate(change);
        }
    };
    const pauseWithUpdateOnCaseExit = updateTypesMap &&
        updateType &&
        updateType.pauseWithUpdateOnCaseExit &&
        updateTypesMap[updateType.pauseWithUpdateOnCaseExit];
    const getLabel = () => {
        return label === null ? updateType && updateType.label : label;
    };
    const inWorkflows = workflows &&
        workflows.filter((workflow) => workflow.tasks.find((task) => task.updateTypeId === id));
    return (React.createElement(React.Fragment, null,
        React.createElement(Panel, { header: React.createElement("div", null,
                lang.updatesEditor.update,
                ' ',
                saveMutation.isLoading && (React.createElement(UpdateTypeSettingsReportSection, { width: "20px" }))) },
            inWorkflows && !!inWorkflows.length && (React.createElement(Section, { header: lang.updatesEditor.information, description: lang.updatesEditor.informationDescription },
                React.createElement("ul", null, inWorkflows.map((workflow) => (React.createElement(UpdateTypeSettingsReportSection2, { key: workflow.workflowId, onClick: () => c.redirect.redirect(`/workflows/${workflow.workflowId}`) },
                    workflow.title,
                    " ",
                    React.createElement(UpdateTypeSettingsReportSection3, null))))))),
            React.createElement(Section, { header: lang.updatesEditor.standardSettings, description: lang.updatesEditor.standardSettingsDescription }, updateType && (React.createElement("div", null,
                React.createElement(SectionContentPanel, null,
                    React.createElement("h3", null, lang.updatesEditor.label),
                    React.createElement(Input, { value: getLabel() || '', onChange: (e) => setLabel(e.target.value), placeholder: "Label", autofocus: true, disabled: !editable, onKeyDown: (e) => {
                            if (e.key === 'Enter' &&
                                updateType &&
                                getLabel() !== updateType.label) {
                                save({ label: getLabel() || '' });
                            }
                        }, button: updateType && getLabel() !== updateType.label
                            ? {
                                value: lang.updatesEditor.save,
                                onClick: () => save({ label: getLabel() || '' }),
                            }
                            : undefined })),
                React.createElement(SectionContentPanel, null,
                    React.createElement("h3", null, lang.updatesEditor.important),
                    React.createElement(Checkbox, { checked: updateType.important, placeholder: lang.updatesEditor.importantPlaceholder, disabled: !editable, onChange: (e) => save({
                            important: e.target.checked,
                            importantTheme: e.target.checked ? 'WARNING' : null,
                        }) })),
                React.createElement(SectionContentPanel, null,
                    React.createElement("h3", null, lang.updatesEditor.pausing),
                    React.createElement(Checkbox, { checked: updateType.pausing, placeholder: lang.updatesEditor.pausingPlaceholder, disabled: !editable, onChange: (e) => save({
                            pausing: e.target.checked,
                        }) })),
                React.createElement(SectionContentPanel, null,
                    React.createElement("h3", null, lang.updatesEditor.selectable),
                    React.createElement(Checkbox, { checked: updateType.selectable, placeholder: lang.updatesEditor.selectablePlaceholder, disabled: !editable, onChange: (e) => save({
                            selectable: e.target.checked,
                        }) })),
                React.createElement(DeadlinePanel, { key: `${updateType.updateTypeId}:${updateType.deadlineDays ?? 'none'}`, deadlineDays: updateType.deadlineDays ?? null, onSave: (deadlineDays) => save({ deadlineDays }) })))),
            (updateType?.procedureEnabled || editable) && (React.createElement(Section, { header: lang.updatesEditor.procedure, description: lang.updatesEditor.procedureDescription }, updateType && (React.createElement(SectionContentPanel, null,
                React.createElement("h3", null,
                    lang.updatesEditor.procedureOn,
                    ' ',
                    updateType.procedureEnabled && (React.createElement(UpdateTypeSettingsMetricText, null,
                        React.createElement(UpdateTypeSettingsReportSection4, null)))),
                updateType.procedure && (React.createElement(Procedure, { procedure: updateType.procedure, onChange: (procedure) => {
                        const toProcedureEnabled = !!(procedure && procedure.length > 0);
                        let procedureEnabled;
                        if (toProcedureEnabled !== updateType.procedureEnabled) {
                            procedureEnabled = toProcedureEnabled;
                        }
                        save({
                            procedure: procedure,
                            procedureEnabled,
                        });
                    } })),
                React.createElement("br", null),
                editable && (React.createElement(ProcedureCreator, { onCreate: (todos) => {
                        let procedureEnabled;
                        if (!updateType.procedureEnabled) {
                            procedureEnabled = true;
                        }
                        save({
                            procedure: (updateType.procedure || []).concat(todos),
                            procedureEnabled,
                        });
                    } })),
                React.createElement("br", null))))),
            React.createElement(Section, { header: lang.updatesEditor.onCaseExit, description: lang.updatesEditor.onCaseExitDescription }, updateType && (React.createElement(React.Fragment, null,
                React.createElement(SectionContentPanel, null,
                    React.createElement("h3", null, lang.updatesEditor.onCaseExitPauseWith),
                    React.createElement(UpdateTypeSelect, { value: pauseWithUpdateOnCaseExit
                            ? pauseWithUpdateOnCaseExit.updateTypeId
                            : null, onChange: (updateTypeId) => {
                            save({
                                pauseWithUpdateOnCaseExit: updateTypeId,
                            });
                        }, disabled: !editable, filter: (_updateType) => _updateType.updateTypeId !== id &&
                            !_updateType.procedure?.some((procedure) => procedure.type === TodoType.ChangeCaseStatus), placeholder: lang.updatesEditor.selectUpdateType })),
                React.createElement(SectionContentPanel, null,
                    React.createElement("h3", null, lang.updatesEditor.onCaseExitContinueUpdates),
                    React.createElement(Checkbox, { checked: updateType.continueOnCaseExit, placeholder: lang.updatesEditor.continueOnCaseExit, disabled: !editable, onChange: (e) => save({
                            continueOnCaseExit: e.target.checked,
                        }) }))))),
            React.createElement(Section, { header: lang.updatesEditor.roles, description: lang.updatesEditor.rolesDescription }, updateType && (React.createElement("div", null,
                React.createElement(SectionContentPanel, null,
                    React.createElement("h3", null, lang.updatesEditor.responsibleRole),
                    React.createElement(Select, { value: updateType && updateType.responsiblePermissionLevel, onChange: (e) => {
                            save({
                                responsiblePermissionLevel: e.target.value || null,
                            });
                        }, disabled: !editable },
                        React.createElement("option", null, " - "),
                        ['client', 'caseworker', 'admin'].map((role) => (React.createElement("option", { key: role, value: role }, lang.roles[role]))))),
                React.createElement(SectionContentPanel, null,
                    React.createElement("h3", null, lang.updatesEditor.hiddenForCreditors),
                    React.createElement(Checkbox, { checked: !!(updateType &&
                            updateType.hiddenPermissionLevel === 'client'), onChange: (e) => {
                            save({
                                hiddenPermissionLevel: e.target.checked ? 'client' : null,
                            });
                        }, disabled: !editable, placeholder: lang.updatesEditor.hiddenForCreditorsPlaceholder }))))),
            (editable ||
                updateType.introductionHTML ||
                updateType.introductionHeroFile) && (React.createElement(Section, { header: lang.updatesEditor.introduction, description: lang.updatesEditor.introductionDescription }, updateType && (React.createElement("div", null,
                React.createElement(SectionContentPanel, null,
                    React.createElement("h3", null, lang.updatesEditor.introductionHeaderImage),
                    React.createElement(UpdateTypeSettingsReportSection5, { onChange: (file) => {
                            if (editable) {
                                save({
                                    introductionHeroFile: file,
                                });
                            }
                        }, dropzoneRef: dropzoneRef }, updateType.introductionHeroFile ? (React.createElement(File, { file: updateType.introductionHeroFile, onRemove: () => save({
                            introductionHeroFile: null,
                        }), removable: true })) : (React.createElement(UpdateTypeSettingsDescription, null, lang.updatesEditor.introductionHeaderImageDropText))),
                    updateType.introductionHTML && (React.createElement(React.Fragment, null,
                        React.createElement(Well, null,
                            React.createElement(UpdateTypeSettingsReportSection6, { onClick: () => save({
                                    introductionHTML: null,
                                }) }),
                            React.createElement("div", { dangerouslySetInnerHTML: {
                                    __html: updateType.introductionHTML,
                                } })),
                        React.createElement("br", null))),
                    React.createElement(Button, { theme: "secondary", onClick: () => setHeroTextEditModalOpen(true), disabled: !editable }, "Redig\u00E9r tekst"),
                    heroTextEditModalOpen && (React.createElement(Modal, { header: ' ', onToggle: () => setHeroTextEditModalOpen(!heroTextEditModalOpen) },
                        React.createElement(WYSIWYGEditor, { onChange: (html) => {
                                save({
                                    introductionHTML: html,
                                });
                                setHeroTextEditModalOpen(false);
                            }, html: updateType.introductionHTML }))),
                    React.createElement("br", null),
                    React.createElement("br", null))))))),
        !props.preventDelete && (React.createElement(Button, { size: "small", theme: "dangerous", loading: deleteMutation.isLoading, disabled: inWorkflows ? inWorkflows.length > 0 : true, tooltip: inWorkflows && inWorkflows.length > 0
                ? 'Cannot delete this update because it is being used in a workflow.'
                : undefined, onClick: () => {
                if (updateType && confirm(lang.updatesEditor.deleteWarning)) {
                    deleteMutation.mutate();
                }
            } }, lang.updatesEditor.deleteButton))));
};
export default UpdateTypeSettings;
const DeadlinePanel = (props) => {
    const lang = useSelector((s) => s.lang);
    const [draft, setDraft] = useState(props.deadlineDays !== null ? String(props.deadlineDays) : '');
    const persisted = props.deadlineDays !== null ? String(props.deadlineDays) : '';
    const isDirty = draft !== persisted;
    const parsed = draft === '' ? null : Number(draft);
    const isInvalid = parsed !== null &&
        (!Number.isInteger(parsed) || parsed < 0 || parsed > 2147483647);
    return (React.createElement(SectionContentPanel, null,
        React.createElement("h3", null, lang.updateTypeSettings.deadlineTitle),
        React.createElement(DeadlinePanelDescription, null, lang.updateTypeSettings.deadlineDescription),
        React.createElement(Input, { type: "number", min: 0, value: draft, onChange: (e) => setDraft(e.target.value), placeholder: lang.updateTypeSettings.deadlinePlaceholder, button: isDirty && !isInvalid
                ? {
                    value: lang.updateTypeSettings.deadlineSave,
                    onClick: () => props.onSave(parsed),
                }
                : undefined })));
};
const UpdateTypeSettingsReportSection = styled(Loading)({
    float: 'right',
    cursor: 'pointer',
});
const UpdateTypeSettingsReportSection2 = styled.li({ cursor: 'pointer' });
const UpdateTypeSettingsReportSection3 = styled(ExternalLink)({
    margin: '0.1em 0 -0.1em',
});
const UpdateTypeSettingsMetricText = styled.span({ color: THEME.COLOR_ACCENT });
const UpdateTypeSettingsReportSection4 = styled(Zap)({ float: 'right' });
const UpdateTypeSettingsReportSection5 = styled(FileDropzone)({
    padding: '40px',
    margin: '20px 0',
    border: `2px dashed ${THEME.BORDER_STRONG}`,
});
const UpdateTypeSettingsDescription = styled.p({ textAlign: 'center' });
const UpdateTypeSettingsReportSection6 = styled(X)({
    float: 'right',
    cursor: 'pointer',
});
const DeadlinePanelDescription = styled.p({
    color: THEME.COLOR_GREY,
    marginTop: 0,
});
//# sourceMappingURL=UpdateTypeSettings.js.map